import {GripVertical} from 'lucide-react';
import {draggable, dropTargetForElements,} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {setCustomNativeDragPreview} from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {pointerOutsideOfPreview} from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import {combine} from '@atlaskit/pragmatic-drag-and-drop/combine';
import React, {useEffect, useRef, useState} from 'react';
import invariant from 'tiny-invariant';
import {createPortal} from 'react-dom';
import {attachClosestEdge, type Edge, extractClosestEdge,} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import {Box, Paper, type PaperProps, Stack, SvgIcon} from '@mui/material';

type DraggableItemState =
    | {
    type: 'idle';
}
    | {
    type: 'preview';
    container: HTMLElement;
}
    | {
    type: 'is-dragging';
}
    | {
    type: 'is-dragging-over';
    closestEdge: Edge | null;
};

function DropIndicator({edge}: { edge: Edge }) {
    return (
        <Box
            sx={{
                position: 'absolute',
                [edge === 'top' ? 'top' : 'bottom']: -2,
                left: 0,
                right: 0,
                height: 2,
                backgroundColor: 'primary.main',
                zIndex: 1,
                mr: "20px",
                ml: "20px",
            }}
        />
    );
}

interface DraggableItemProps extends PaperProps {
    item: any;
    itemId: number;
    onDrop?: () => void;
}

export function DraggableItem({item, itemId, onDrop, ...props}: DraggableItemProps) {
    const ref = useRef<HTMLDivElement | null>(null);
    const [state, setState] = useState<DraggableItemState>({type: 'idle'});

    useEffect(() => {
        const element = ref.current;
        invariant(element);

        return combine(
            draggable({
                element,
                getInitialData: () => ({id: itemId, type: 'item'}),
                onGenerateDragPreview: ({nativeSetDragImage}) => {
                    setCustomNativeDragPreview({
                        nativeSetDragImage,
                        getOffset: pointerOutsideOfPreview({x: "16", y: "8"}),
                        render: ({container}) => setState({type: 'preview', container}),
                    });
                },
                onDragStart: () => setState({type: 'is-dragging'}),
                onDrop: () => {
                    setState({type: 'idle'});
                    onDrop?.();
                },
            }),
            dropTargetForElements({
                element,
                canDrop: ({source}) => source.element !== element,
                getData: ({input}) =>
                    attachClosestEdge({id: itemId, type: 'item'}, {
                        element,
                        input,
                        allowedEdges: ['top', 'bottom'],
                    }),
                getIsSticky: () => true,
                onDragEnter: ({self}) => {
                    const closestEdge = extractClosestEdge(self.data);
                    setState({type: 'is-dragging-over', closestEdge});
                },
                onDrag: ({self}) => {
                    const closestEdge = extractClosestEdge(self.data);
                    setState(prev =>
                        prev.type === 'is-dragging-over' && prev.closestEdge === closestEdge
                            ? prev
                            : {type: 'is-dragging-over', closestEdge}
                    );
                },
                onDragLeave: () => setState({type: 'idle'}),
                onDrop: () => setState({type: 'idle'}),
            })
        );
    }, [itemId, item, onDrop]);

    return (
        <Box sx={{position: 'relative',}}>
            {ref && (
                <>
                    <Paper
                        ref={ref}
                        data-item-id={itemId}
                        elevation={2}
                        sx={{
                            transition: 'opacity 0.2s, background-color 0.2s',
                            opacity: state.type === 'is-dragging' ? 0.4 : 1,
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                cursor: 'grab',
                            },
                            ...props.sx,
                        }}
                        {...props}
                    >
                        <Stack direction="row" alignItems={'center'}>
                            <SvgIcon component={GripVertical} viewBox="0 0 24 24" fontSize="small"/>
                            {props.children}
                        </Stack>
                    </Paper>

                    {state.type === 'is-dragging-over' && state.closestEdge && (
                        <DropIndicator edge={state.closestEdge}/>
                    )}

                    {state.type === 'preview' && createPortal(
                        <Paper elevation={3} sx={{minWidth: 120}}>
                            {props.children}
                        </Paper>,
                        state.container
                    )}

                </>
            )
            }
        </Box>
    );
}
